import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { PurchaseOrderActivityDetailState } from '../types/purchase-order-activity-item';

export function PurchaseOrderActivityDetailDialog({
  state,
  onOpenChange,
}: {
  state: PurchaseOrderActivityDetailState;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation('purchaseOrder');

  return (
    <Dialog onOpenChange={onOpenChange} open={Boolean(state)}>
      <DialogContent
        className='overflow-hidden rounded-xl p-0 sm:max-w-4xl'
        showCloseButton={false}
      >
        <div className='max-h-[85vh] overflow-y-auto px-8 py-7'>
          <DialogHeader className='mb-6 flex-row items-start justify-between gap-4 text-left'>
            <div className='space-y-2'>
              <DialogTitle className='text-[22px] leading-tight'>
                {state?.title}
              </DialogTitle>
              {state?.description ? (
                <DialogDescription className='text-base leading-none'>
                  {state.description}
                </DialogDescription>
              ) : null}
            </div>
            <DialogClose asChild>
              <button
                aria-label={t('close', { ns: 'common', defaultValue: 'Close' })}
                className='-mt-2 -mr-2 inline-flex size-10 items-center justify-center rounded-md text-foreground transition-colors hover:bg-muted'
                type='button'
              >
                <X className='size-7' />
              </button>
            </DialogClose>
          </DialogHeader>
          <div className='overflow-hidden rounded-xl border'>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('products.sku')}</TableHead>
                  <TableHead>{t('activity.line.field')}</TableHead>
                  <TableHead>{t('activity.line.oldValue')}</TableHead>
                  <TableHead>{t('activity.line.newValue')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {state?.rows.map((row) => (
                  <TableRow
                    key={`${row?.lineId ?? row?.sku ?? 'line'}-${row?.fieldKey ?? 'field'}-${row?.oldValue ?? ''}-${row?.newValue ?? ''}`}
                  >
                    <TableCell className='font-medium'>{row?.sku}</TableCell>
                    <TableCell className='whitespace-normal'>
                      {t(`syncInformationsFields.${row?.fieldKey}`, {
                        defaultValue: row?.fieldKey,
                      })}
                    </TableCell>
                    <TableCell className='whitespace-normal text-destructive'>
                      {row?.oldValue}
                    </TableCell>
                    <TableCell className='whitespace-normal'>
                      {row?.newValue}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
